import React, { useCallback, useEffect, useState } from 'react';
import { useDataProvider, useNotify } from 'react-admin';

import Box from '@material-ui/core/Box';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import CircularProgress from '@material-ui/core/CircularProgress';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import Chip from '@material-ui/core/Chip';
import Divider from '@material-ui/core/Divider';
import formatPrice, { formatCentsPrice } from 'services/helpers/formatPrice';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },

  section: {
    marginBottom: theme.spacing(3),
  },

  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
  },

  warehouseCard: {
    marginBottom: theme.spacing(2),
  },

  warehouseHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },

  warehouseLocation: {
    fontWeight: 600,
    fontSize: '1.1rem',
  },

  warehouseId: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },

  addressLine: {
    marginBottom: theme.spacing(0.5),
  },

  statusChip: {
    marginLeft: theme.spacing(1),
  },

  cardInfo: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    gap: '12px',
  },

  cardInfoContent: {
    display: 'flex',
    alignItems: 'center',
  },

  cardBrand: {
    textTransform: 'capitalize',
    marginRight: theme.spacing(1),
  },

  defaultCard: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },

  autoRefillInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },

  shipmentCard: {
    marginBottom: theme.spacing(1.5),
  },

  shipmentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },

  shipmentProvider: {
    fontWeight: 600,
    fontSize: '1.1rem',
  },

  shipmentStatus: {
    textTransform: 'capitalize',
  },

  shipmentDetails: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },

  shipmentInfo: {
    display: 'flex',
    gap: '100px',
  },

  shipmentMetric: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },

  metricValue: {
    fontWeight: 600,
    fontSize: '1.2rem',
    color: theme.palette.primary.main,
  },

  metricLabel: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },

  summaryText: {
    color: theme.palette.primary.main,
    fontWeight: 400,
  },

  riskProfileContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
}));

const UserDetailsTab = ({ user }) => {
  console.log('user --->', user);
  const classes = useStyles();

  const dataProvider = useDataProvider();
  const notify = useNotify();

  const { id: userId, currency, billingUserId } = user;

  const [loading, setLoading] = useState(false);
  const [userDetails, setUserDetails] = useState<any>(null);
  const [achData, setAchData] = useState<any>(null);

  const getUserDetails = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await dataProvider.getNest('userDetails', {
        userId,
        billingUserId,
        currency,
      });
      setUserDetails(data);
    } catch (error: any) {
      notify(error?.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [dataProvider, userId, billingUserId, currency, notify]);

  useEffect(() => {
    getUserDetails();
  }, [getUserDetails]);

  useEffect(() => {
    const getAchData = async () => {
      try {
        const { data } = await dataProvider.getOne('userRiskProfile', {
          id: billingUserId,
        });

        setAchData(data);
      } catch (error: any) {
        notify(error?.message ?? error, 'error');
      }
    };

    getAchData();
  }, [billingUserId, dataProvider, notify]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderWarehouses = () => {
    if (!userDetails?.warehouses?.length) {
      return <Typography color="textSecondary">No warehouses found</Typography>;
    }

    return userDetails.warehouses.map((warehouse: any) => (
      <Card key={warehouse.id} className={classes.warehouseCard}>
        <CardContent>
          <div className={classes.warehouseHeader}>
            <Typography className={classes.warehouseLocation}>
              {warehouse.city}, {warehouse.stateCode} {warehouse.postalCode}
            </Typography>
            <Typography className={classes.warehouseId}>ID: {warehouse.id}</Typography>
          </div>

          <Typography className={classes.addressLine}>{warehouse.addressLine1}</Typography>
          {warehouse.addressLine2 && (
            <Typography className={classes.addressLine}>{warehouse.addressLine2}</Typography>
          )}
          {warehouse.addressLine3 && (
            <Typography className={classes.addressLine}>{warehouse.addressLine3}</Typography>
          )}

          <Box mt={1}>
            <Typography variant="body2" color="textSecondary">
              Created: {formatDate(warehouse.createdAt)}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Updated: {formatDate(warehouse.updatedAt)}
            </Typography>
            {warehouse.reviewedAt && (
              <Typography variant="body2" color="textSecondary">
                Reviewed: {formatDate(warehouse.reviewedAt)}
              </Typography>
            )}
            {warehouse.deletedAt && (
              <Chip label="Deleted" color="secondary" size="small" className={classes.statusChip} />
            )}
          </Box>
        </CardContent>
      </Card>
    ));
  };

  const renderBillingInfo = () => {
    if (!userDetails?.billingUserSettings) {
      return <Typography color="textSecondary">No billing information found</Typography>;
    }

    const { billingUserSettings } = userDetails;

    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" className={classes.sectionTitle}>
                Auto Refill Settings
              </Typography>
              <div className={classes.autoRefillInfo}>
                <Typography>
                  Status: {billingUserSettings.autoRefill?.enabled ? 'Enabled' : 'Disabled'}
                </Typography>
                {billingUserSettings.autoRefill?.enabled && (
                  <Chip
                    label={billingUserSettings.autoRefill.amount}
                    color="primary"
                    size="small"
                  />
                )}
              </div>
              <Typography variant="body2" color="textSecondary">
                Stripe Customer ID: {billingUserSettings.stripeCustomerId}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" className={classes.sectionTitle}>
                Payment Cards ({billingUserSettings.cards?.length || 0})
              </Typography>
              {billingUserSettings.cards?.map((card: any) => (
                <div key={card.cardId} className={classes.cardInfo}>
                  <div className={classes.cardInfoContent}>
                    <Chip label={card.brand} className={classes.cardBrand} size="small" />
                    <Typography>•••• {card.lastDigits}</Typography>
                  </div>
                  {card.cardId === billingUserSettings.defaultCardId && (
                    <Chip label="Default" className={classes.defaultCard} size="small" />
                  )}
                  {card.failedLastTime && <Chip label="Failed" color="secondary" size="small" />}
                </div>
              ))}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" className={classes.sectionTitle}>
                Bank Accounts ({billingUserSettings.usBankAccounts?.length || 0})
              </Typography>
              {billingUserSettings.usBankAccounts?.map((account: any) => (
                <div key={account.paymentMethodId} className={classes.cardInfo}>
                  <div className={classes.cardInfoContent}>
                    <Chip label={account.bankName} className={classes.cardBrand} size="small" />
                    <Typography>•••• {account.lastDigits}</Typography>
                  </div>
                  {account.paymentMethodId === billingUserSettings.defaultUsBankAccount && (
                    <Chip label="Default" className={classes.defaultCard} size="small" />
                  )}
                  {account.failedLastTime && <Chip label="Failed" color="secondary" size="small" />}
                </div>
              ))}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderShipmentSummaries = () => {
    if (!userDetails?.shipmentSummaries?.length) {
      return <Typography color="textSecondary">No shipment summaries found</Typography>;
    }

    const getStatusColor = (status: string) => {
      switch (status.toUpperCase()) {
        case 'SHIPPED':
          return 'primary';
        case 'REFUNDED':
          return 'secondary';
        case 'REFUND_REQUESTED':
          return 'default';
        default:
          return 'default';
      }
    };

    return (
      <>
        {userDetails.shipmentSummaries.map((summary: any, index: number) => (
          <Card key={index} className={classes.shipmentCard}>
            <CardContent>
              <div className={classes.shipmentHeader}>
                <Typography className={classes.shipmentProvider}>{summary.provider}</Typography>
                <Chip
                  label={summary.status.replace(/_/g, ' ')}
                  color={getStatusColor(summary.status)}
                  size="small"
                  className={classes.shipmentStatus}
                />
              </div>

              <div className={classes.shipmentDetails}>
                <div className={classes.shipmentInfo}>
                  <div className={classes.shipmentMetric}>
                    <Typography className={classes.metricLabel}>
                      {summary.labels === 1 ? 'Label' : 'Labels'}
                    </Typography>
                    <Typography className={classes.metricValue}>{summary.labels}</Typography>
                  </div>

                  <div className={classes.shipmentMetric}>
                    <Typography className={classes.metricLabel}>Amount</Typography>
                    <Typography className={classes.metricValue}>
                      {formatPrice(summary.amount.toFixed(2), { currency })}
                    </Typography>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </>
    );
  };

  const getShipmentSummaryTitle = () => {
    if (!userDetails?.shipmentSummaries?.length) {
      return 'Shipment Summaries (0)';
    }

    const totals = userDetails.shipmentSummaries.reduce(
      (acc: any, summary: any) => {
        acc.totalLabels += summary.labels;
        acc.totalAmount += summary.amount;
        return acc;
      },
      { totalLabels: 0, totalAmount: 0 }
    );

    return (
      <>
        Shipment Summaries ({userDetails.shipmentSummaries.length}) -{' '}
        <span className={classes.summaryText}>
          {totals.totalLabels} Labels, {formatPrice(totals.totalAmount.toFixed(2), { currency })}
        </span>
      </>
    );
  };

  const renderRiskProfile = () => {
    return (
      <Card>
        <CardContent>
          <Box className={classes.riskProfileContainer}>
            <Typography>Risk tier: {achData?.riskTier}</Typography>

            <Typography>
              Instant availabiliy cap: {formatCentsPrice(achData?.instantAvailabilityCapCents)}
            </Typography>

            <Typography>
              Instant used amount: {formatCentsPrice(achData?.instantUsedAmountCents)}
            </Typography>

            <Typography>
              Unsettled amount: {formatCentsPrice(achData?.unsettledAmountCents)}
            </Typography>

            <Typography>
              Total ACH amount: {formatCentsPrice(achData?.totalAchAmountCents)}
            </Typography>

            <Typography>
              Successful Clearances count: {achData?.successfulClearancesCount}
            </Typography>

            <Typography>Unsettled count: {achData?.unsettledCount}</Typography>

            <Typography>
              Last successful Clearance at:{' '}
              {achData?.lastSuccessfulClearanceAt
                ? formatDate(achData?.lastSuccessfulClearanceAt)
                : '-'}
            </Typography>

            <Typography>
              Last unsuccessful Clearance at:{' '}
              {achData?.lastUnsuccessfulClearanceAt
                ? formatDate(achData?.lastUnsuccessfulClearanceAt)
                : '-'}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Box className={classes.root}>
      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : userDetails ? (
        <>
          <div className={classes.section}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Billing Information
            </Typography>
            {renderBillingInfo()}
          </div>

          <Divider />

          <div className={classes.section}>
            <Typography variant="h6" className={classes.sectionTitle}>
              ACH Risk Profile
            </Typography>
            {renderRiskProfile()}
          </div>

          <Divider />

          <div className={classes.section}>
            <Typography variant="h6" className={classes.sectionTitle}>
              {getShipmentSummaryTitle()}
            </Typography>
            {renderShipmentSummaries()}
          </div>

          <Divider />

          <div className={classes.section}>
            <Typography variant="h6" className={classes.sectionTitle}>
              Warehouses ({userDetails.warehouses?.length || 0})
            </Typography>
            {renderWarehouses()}
          </div>
        </>
      ) : (
        <Typography color="textSecondary">No user details available</Typography>
      )}
    </Box>
  );
};

export default UserDetailsTab;
